import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { t } from 'i18next';
import useChatRoomMessages from '../../components/api/queries/useCharRoomMessages';
import { useOptimisticChatMessages } from './hooks/useOptimisticChatMessages';
import { useInfiniteScroll } from './hooks/useInfiniteScroll';
import { queryGet } from '../../utils/queryClient';
import { attachJosa, getGroupFlags } from '../../utils/bettieUtils';
import BettieMessageInput from './components/BettieMessageInput';
import StickyHeader from '../components/bar/StickyHeader';
import MessageBubble from './components/MessageBubble';
import ProfileAvatar from '../my-page/components/ProfileAvatar';
import type { components } from '../../types/schema';
import { useFeatureEnabled } from '../../domains/features/ui/hooks/useFeatureEnabled';
import { FEATURE_KEYS } from '../../domains/features/constants';
import { More, Xmark } from '@peacecover/icon';
import BettieChatRoomSettingBottomDrawer from '../../components/drawer/BettieChatRoomSettingBottomDrawer';
import { useGetUserPreferences } from '../../domains/user/ui/hooks/useGetUserPreferences';
import PopUpBasic from '../components/popup/PopUpBasic';
import leaveChatRoom from '../../components/api/mutations/legacy/leaveChatRoom';
import GradientLoadingSpinner from '../components/GradientLoadingSpinner';
import BettieProfileUpdateBottomDrawer from '../../components/drawer/BettieProfileUpdateBottomDrawer';
import BettieNicknameUpdateBottomDrawer from '../../components/drawer/BettieNicknameUpdateBottomDrawer';
import {
  deleteBettieChatMessage,
  resendBettieChatMessage
} from '../../domains/bettie/services/bettieService';
import { ReactComponent as GradientChatFill } from '../../icons/gradient.chat.fill.svg';
import BettieChargeBottomSheet from '../../components/bottomSheet/bettie/BettieChargeBottomSheet';
import { useSequentialBettieReplies } from './hooks/useSequentialBettieReplies';
import { RoundBadge } from '@peacecover/component';

type BettieMessage = components['schemas']['ChatRoomMessage'];
type ChatRoomMessagesPage = { chatRoomMessages?: BettieMessage[] };
type BettieChatRoom = components['schemas']['ChatRoom'];

const BettieChatRoomPage = () => {
  const { chatRoomId } = useParams<{ chatRoomId: string }>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLeaveChatRoomPopupOpen, setIsLeaveChatRoomPopupOpen] = useState(false);
  const [isProfileUpdateSheetOpen, setIsProfileUpdateSheetOpen] = useState(false);
  const [isNicknameUpdateSheetOpen, setIsNicknameUpdateSheetOpen] = useState(false);
  const [isFailedMessagePopupOpen, setIsFailedMessagePopupOpen] = useState(false);
  const [isChargeBottomSheetOpen, setIsChargeBottomSheetOpen] = useState(false);
  const [failedMessage, setFailedMessage] = useState({
    id: '',
    content: ''
  });
  const [freeChatCount, setFreeChatCount] = useState(0);
  const [isLetterBannerOpen, setIsLetterBannerOpen] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // 초기 스크롤이 완료되었는지 확인하는 Ref
  const hasScrolledInitially = useRef(false);

  // 베타 유저 확인
  const { data: isBettieBetaUser } = useFeatureEnabled(FEATURE_KEYS.BETTIE_BETA_USER);
  const isBettieBetaUserEnabled = isBettieBetaUser?.featureFlag.enabled;

  const { data: userPreferences } = useGetUserPreferences({ enabled: true });
  const isBettieChatAlarm = userPreferences?.userPreferences.alert?.bettieChat ?? true; // null일 경우 알림이 켜져있는 것으로 간주

  const { data: chatRoom, isLoading: isChatRoomLoading } = useQuery(
    ['bettieChatRoom', chatRoomId],
    () => queryGet(`/api/v1/chat_rooms/${chatRoomId}`),
    {
      enabled: !!chatRoomId,
      select: (r: { chatRoom: BettieChatRoom }) => r.chatRoom,
      cacheTime: 0
    }
  );

  const { data, isFetching, fetchNextPage, hasNextPage, refetch } = useChatRoomMessages(
    chatRoomId,
    50
  );

  const messages: BettieMessage[] = useMemo(
    () =>
      (data?.pages as ChatRoomMessagesPage[] | undefined)
        ?.flatMap((page) => page.chatRoomMessages ?? [])
        .reverse() || [],
    [data]
  );

  const { displayMessages, onBeforeSend, onAfterSent, onSendFailed, lastKey } =
    useOptimisticChatMessages({
      baseMessages: messages,
      chatRoom,
      chatRoomId
    });

  // 스크롤 이벤트 처리
  useInfiniteScroll({
    fetchNextPage,
    isFetching,
    hasNextPage: hasNextPage ?? false,
    data: messages
  });

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, []);

  const { renderMessages, startSequentialReveal } = useSequentialBettieReplies({
    baseMessages: displayMessages,
    scrollToBottom
  });

  // 최초 진입 시점에만 스크롤을 맨 아래로 내림
  useEffect(() => {
    const isInitialLoadCompleted = !isChatRoomLoading && messages.length > 0;

    // 아직 초기 스크롤을 하지 않은 경우에만 실행
    if (isInitialLoadCompleted && !hasScrolledInitially.current) {
      // DOM 렌더링이 완료된 후 스크롤을 보장하기 위해 setTimeout을 사용
      setTimeout(() => {
        scrollToBottom('auto');
        hasScrolledInitially.current = true;
      }, 0);
    }
  }, [isChatRoomLoading, messages.length, scrollToBottom]);

  // 마지막 메시지가 변경되었을 때 스크롤 위치 복원
  useEffect(() => {
    if (!lastKey) return;
    scrollToBottom('smooth');
  }, [lastKey, scrollToBottom]);

  useEffect(() => {
    if (chatRoom) {
      setFreeChatCount(chatRoom.bettieChatsCount);
    }
  }, [chatRoom]);

  const handleLeaveChatRoom = async () => {
    const response = await leaveChatRoom(chatRoomId);
    if (response.success) {
      setIsLeaveChatRoomPopupOpen(false);
      queryClient.invalidateQueries(['AllChatRooms', 'only_joined:true']);
      queryClient.invalidateQueries(['bettieBalances']);
      navigate('/home', { replace: true });
    }
  };

  const handleResendMessage = async (chatRoomMessageId: string, content: string) => {
    setIsFailedMessagePopupOpen(false);
    // 낙관적 업데이트 처리
    onBeforeSend(content);
    try {
      const data = await resendBettieChatMessage(chatRoomMessageId);
      if (data?.userMessage) {
        await refetch();
        onAfterSent();
      }
      if (data.meta.bettieChatsCount !== undefined && data.meta.bettieChatsCount !== null) {
        setFreeChatCount((prev) => Math.min(prev, data.meta.bettieChatsCount as number));
      }
    } catch (error) {
      onSendFailed();
      console.error(error);
    }
  };

  const handleDeleteMessage = async (chatRoomMessageId: string) => {
    try {
      await deleteBettieChatMessage(chatRoomMessageId);
      refetch();
    } catch (error) {
      console.error(error);
    } finally {
      setIsFailedMessagePopupOpen(false);
    }
  };

  if (isChatRoomLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <GradientLoadingSpinner size={36} />
      </div>
    );
  }

  if (!chatRoom) {
    throw new Error('Chat room not found');
  }

  const hasMoreFreeChat = freeChatCount > 0;

  return (
    <div className="bg-background-light dark:bg-background-dark">
      <StickyHeader
        title={chatRoom.bettieProfile.nickname}
        showBackButton={true}
        showTitle={true}
        alwaysShowTitle={true}
        rightContent={
          <div className="flex items-center justify-center gap-[10px]">
            <button
              type="button"
              className="flex items-center justify-center gap-[2px] whitespace-nowrap"
              onClick={() => setIsChargeBottomSheetOpen(true)}
            >
              <GradientChatFill width={24} height={24} />
              <p className="text-[16px] font-medium leading-[20px] text-text-main-lv1 whitespace-nowrap">
                {t('BettieChatRoom.chatCount', { count: freeChatCount })}
              </p>
            </button>
            <More size={24} className="text-text-main-lv1" onClick={() => setIsModalOpen(true)} />
          </div>
        }
      />
      {displayMessages.length > 0 ? (
        <>
          <div className="h-[35px] mt-[env(safe-area-inset-top)]" />
          <div className="pt-[16px] pl-[12px] pr-[8px] pb-[140px]">
            {isLetterBannerOpen && (
              <BettieLetterBanner
                subjectId={chatRoom.subjectId ?? ''}
                memberName={chatRoom.bettieProfile.nickname}
                onClose={() => setIsLetterBannerOpen(false)}
              />
            )}
            {isFetching && (
              <div className="flex items-center justify-center gap-[6px] mt-[12px]">
                <GradientLoadingSpinner size={20} />
                <p className="text-[11px] text-text-main-lv2 font-medium">
                  {t('BettieChatRoom.loadingPrevious')}
                </p>
              </div>
            )}
            {renderMessages.map((m, index) => {
              const { previousDate, isFirstMessage, isFirstInGroup, isLastInGroup } = getGroupFlags(
                renderMessages,
                index
              );

              return (
                <div key={m.chatRoomMessageId} id={`message-${m.chatRoomMessageId}`}>
                  <MessageBubble
                    message={m}
                    previousDate={previousDate}
                    isFirstMessage={isFirstMessage}
                    isLastInGroup={isLastInGroup}
                    isFirstInGroup={isFirstInGroup}
                    onProfileImageClick={() => setIsProfileUpdateSheetOpen(true)}
                    isPopupOpen={() => {
                      setFailedMessage({
                        id: m.chatRoomMessageId,
                        content: m.content
                      });
                      setIsFailedMessagePopupOpen(true);
                    }}
                  />
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        </>
      ) : (
        <EmptyBettieChatCase chatRoom={chatRoom} />
      )}

      <div className="fixed pb-[env(safe-area-inset-bottom)] bottom-0 left-0 right-0 shadow-[0_-2px_2px_0_rgba(0,0,0,0.02),0_-3px_8px_0_rgba(0,0,0,0.03)] dark:shadow-[0_-2px_2px_0_rgba(0,0,0,0.12),0_-3px_8.6px_0_rgba(0,0,0,0.28)] bg-background-light dark:bg-background-dark">
        <BettieMessageInput
          chatRoomUuid={chatRoomId || ''}
          onBeforeSend={(content) => {
            onBeforeSend(content);
          }}
          onMessageSent={async () => {
            await refetch();
            onAfterSent();
          }}
          onBettieReplies={(ids) => {
            const delays: Record<string, number> = {};
            for (const id of ids) {
              delays[id] = 2000; // 2000ms 고정 지연
            }
            startSequentialReveal(ids, delays);
          }}
          onSendFailed={onSendFailed}
          disabled={!isBettieBetaUserEnabled || !hasMoreFreeChat}
          setFreeChatCount={setFreeChatCount}
        />
      </div>
      <BettieChatRoomSettingBottomDrawer
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        isBettieChatAlarm={isBettieChatAlarm}
        leavePopupOpen={() => setIsLeaveChatRoomPopupOpen(true)}
        openProfileUpdateSheet={() => {
          setIsModalOpen(false);
          setIsProfileUpdateSheetOpen(true);
        }}
        openNicknameUpdateSheet={() => {
          setIsModalOpen(false);
          setIsNicknameUpdateSheetOpen(true);
        }}
      />
      <BettieProfileUpdateBottomDrawer
        isOpen={isProfileUpdateSheetOpen}
        onClose={() => setIsProfileUpdateSheetOpen(false)}
        bettieProfile={chatRoom.bettieProfile}
        chatRoomId={chatRoomId as string}
      />
      <BettieNicknameUpdateBottomDrawer
        isOpen={isNicknameUpdateSheetOpen}
        onClose={() => setIsNicknameUpdateSheetOpen(false)}
        bettieProfile={chatRoom.bettieProfile}
        chatRoomId={chatRoomId as string}
        currentNickname={chatRoom.myNickname ?? ''}
      />
      {isLeaveChatRoomPopupOpen && (
        <div className="fixed top-0 left-0 w-full h-full bg-black/50 z-[1000] flex items-center justify-center">
          <PopUpBasic
            type="short"
            title={t('BettieChatRoom.leaveChatRoomPopup.title')}
            description={t('BettieChatRoom.leaveChatRoomPopup.description')}
            primaryButtonText={t('BettieChatRoom.leaveChatRoomPopup.cancelButton')}
            onPrimaryButtonClick={() => {
              setIsLeaveChatRoomPopupOpen(false);
            }}
            secondaryButtonText={t('BettieChatRoom.leaveChatRoomPopup.leaveButton')}
            onSecondaryButtonClick={handleLeaveChatRoom}
            className="w-[311px]"
          />
        </div>
      )}
      {isFailedMessagePopupOpen && (
        <button
          type="button"
          className="fixed top-0 left-0 w-full h-full bg-black/50 z-[1000] flex items-center justify-center"
          onClick={() => setIsFailedMessagePopupOpen(false)}
          aria-label="close failed message popup"
        >
          <div role="dialog" onMouseDown={(e) => e.stopPropagation()}>
            <PopUpBasic
              type="short"
              title={t('BettieChatRoom.failedMessagePopup.title')}
              description={t('BettieChatRoom.failedMessagePopup.description')}
              primaryButtonText={t('BettieChatRoom.failedMessagePopup.delete')}
              secondaryButtonText={t('BettieChatRoom.failedMessagePopup.resend')}
              onPrimaryButtonClick={() => handleDeleteMessage(failedMessage.id)}
              onSecondaryButtonClick={() =>
                handleResendMessage(failedMessage.id, failedMessage.content)
              }
              className="w-[311px] z-[2000]"
            />
          </div>
        </button>
      )}
      {isChargeBottomSheetOpen && (
        <BettieChargeBottomSheet
          isOpen={isChargeBottomSheetOpen}
          onClose={() => setIsChargeBottomSheetOpen(false)}
        />
      )}
    </div>
  );
};

export default BettieChatRoomPage;

const EmptyBettieChatCase = ({ chatRoom }: { chatRoom: BettieChatRoom }) => {
  return (
    <div className="mt-[calc(247px+env(safe-area-inset-top))] flex flex-col items-center gap-[16px]">
      <ProfileAvatar
        thumbnailUrl={chatRoom.bettieProfile.pictureUrl}
        widthAndHeight="84px"
        svgWidthAndHeight="56px"
      />
      <div className="px-[22px] pt-[15px] pb-[16px] bg-separator1 rounded-[20px] shadow-[0_0_3px_0_rgba(0,0,0,0.05),0_0_12px_0_rgba(0,0,0,0.05)]">
        <p className="text-[13px] font-semibold leading-[16px] text-text-main-lv2 whitespace-pre-line break-words">
          {t('BettieChatRoom.chatWithBettie', {
            nickname: attachJosa(chatRoom.bettieProfile.nickname, '과/와')
          })}
        </p>
      </div>
    </div>
  );
};

const BettieLetterBanner = ({
  subjectId,
  memberName,
  onClose
}: {
  subjectId: string;
  memberName: string;
  onClose: () => void;
}) => {
  const isBasicPersona = subjectId === '16e2334d-9f56-4695-9bee-247c92b7b100';

  const warningText = t('BettieChatRoom.letterBanner.warningText', {
    nickname: memberName
  });

  return (
    <div className="fixed top-[calc(env(safe-area-inset-top)+64px)] left-0 right-0 flex items-center mx-[8px] justify-between bg-background-elevated py-[12px] pr-[15px] pl-[12px] rounded-[12px] shadow-[0_0_4px_0_rgba(0,0,0,0.05),_0_0_11.6px_0_rgba(0,0,0,0.05)] z-50">
      <div className="flex items-center gap-[8px] flex-1 min-w-0 overflow-hidden">
        {isBasicPersona ? (
          <div className="w-[24px] h-[24px] flex items-center justify-center bg-surface-lv2 rounded-full flex-shrink-0">
            <img src="/assets/icons/warning.svg" alt="" className="w-[17px] h-[14px]" />
          </div>
        ) : (
          <RoundBadge colorVariant="secondary">Tip</RoundBadge>
        )}

        {isBasicPersona ? (
          // isBasicPersona일 때: 무한 스크롤 적용
          <div className="flex w-full overflow-hidden mask-image-linear-gradient">
            <div className="infinite-scroll flex flex-shrink-0 gap-4 pr-4 whitespace-nowrap">
              <p className="text-[13px] font-medium leading-[16px] text-text-main-lv3">
                {warningText}
              </p>
              <p className="text-[13px] font-medium leading-[16px] text-text-main-lv3">
                {warningText}
              </p>
            </div>
            <div
              className="infinite-scroll flex flex-shrink-0 gap-4 pr-4 whitespace-nowrap"
              aria-hidden="true"
            >
              <p className="text-[13px] font-medium leading-[16px] text-text-main-lv3">
                {warningText}
              </p>
              <p className="text-[13px] font-medium leading-[16px] text-text-main-lv3">
                {warningText}
              </p>
            </div>
          </div>
        ) : (
          <p className="text-[13px] font-medium leading-[16px] text-text-main-lv3 truncate">
            {t('BettieChatRoom.letterBanner.message')}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="close letter banner"
        className="flex items-center justify-center flex-shrink-0 ml-[8px]"
      >
        <Xmark size={14} className="text-text-main-lv3" />
      </button>
    </div>
  );
};
